import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  CLEAR_OFFER_CHECK_IN_CHECK_OUT,
  OFFER_CALENDAR_RECOMMENDATIONS_CLEAR,
  REMOVE_DISMISSED_ABANDONED_CART_OFFER_IDS,
  SAVE_DISMISSED_ABANDONED_CART_OFFER_IDS,
  SET_LERE_VERSION,
  SET_OFFER_CHECK_IN_CHECK_OUT,
  SET_OFFER_LERE_INLINE_YMAL_IN_VIEW,
} from 'actions/actionConstants'
import {
  CACHE_LERE_RECOMMENDATIONS,
  DISMISS_LERE_PERSONALISED_ABANDONED_CARTS,
  FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
  FETCH_LERE_HERO_PLANNER,
  FETCH_LERE_HOT_LEADS,
  FETCH_LERE_LOCATION_FLEX_RECOMMENDATIONS,
  FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS,
  FETCH_LERE_PERSONALISED_ABANDONED_CARTS,
  FETCH_LERE_PERSONALISED_CONTEXT,
  FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
  FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME_EXISTS,
  FETCH_LERE_TOP_DEALS,
  FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
  SAVE_LERE_HOT_LEADS,
} from 'actions/apiActionConstants'
import { unique, without } from 'lib/arrayUtils'
import { createReducer, reducerSwitch } from 'lib/reducerUtils'
import { AbandonedCartViewModel } from 'selectors/recommendations'

const emptyHotLead: App.OfferHotLead = {
  offerId: null,
  occupancy: null,
  isDismissed: false,
}

const initialState: App.RecommendationState = {
  lereVersion: undefined,
  lerePrecached: {},
  calendarRecommendations: {
    state: 'loading',
    offerIds: null,
    requestId: -1,
  },
  recommendations: {},
  locationSeededRecommendations: {},
  locationFlexRecommendations: {},
  offerSharedState: {
  },
  personalisedAbandonedCarts: {
    'naive-model': {
      carts: null,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
    'optimised-model': {
      carts: null,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
  },
  personalisedContext: {
    offerIds: null,
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  personalisedPeopleLikeMe: {
    offerIds: null,
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  personalisationPeopleLikeMeExists: {
    exists: null,
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  topDeals: {
    offerIds: null,
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  heroPlanner: {
    offerIds: null,
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  hotLead: {
    data: emptyHotLead,
    state: 'loading',
    error: undefined,
    lereVersion: undefined,
  },
  dismissedAbandonedCart: {
    offerIds: [],
  },
}

const apiRequests = reducerSwitch<App.RecommendationState>({
  [CACHE_LERE_RECOMMENDATIONS]: (state, action) => ({
    ...state,
    lerePrecached: {
      ...state.lerePrecached,
      [action.cacheKey]: {
        state: 'loading',
        startTime: Date.now(),
      },
    },
  }),
  [FETCH_CALENDAR_OFFER_RECOMMENDATIONS]: (state, action) => ({
    ...state,
    calendarRecommendations: {
      state: 'loading',
      offerIds: null,
      requestId: action.requestId,
    },
  }),
  [FETCH_OFFER_BASED_LERE_RECOMMENDATIONS]: (state, action) => {
    if (action.mergeResults && state.recommendations[action.cacheKey]) {
      return state
    }
    return {
      ...state,
      recommendations: {
        ...state.recommendations,
        [action.cacheKey]: {
          offerIds: null,
          state: 'loading',
          filtersFetched: action.filterKey ? [action.filterKey] : [],
        },
      },
    }
  },
  [FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS]: (state, action) => {
    return ({
      ...state,
      locationSeededRecommendations: {
        ...state.locationSeededRecommendations,
        [action.destinationKey]: {
          offerIds: [],
          state: 'loading',
        },
      },
    })
  },
  [FETCH_LERE_LOCATION_FLEX_RECOMMENDATIONS]: (state, action) => {
    return ({
      ...state,
      locationFlexRecommendations: {
        ...state.locationFlexRecommendations,
        [action.cacheKey]: {
          offers: [],
          state: 'loading',
        },
      },
    })
  },
  [FETCH_LERE_PERSONALISED_ABANDONED_CARTS]: (state, action) => {
    return ({
      ...state,
      personalisedAbandonedCarts: {
        ...state.personalisedAbandonedCarts,
        [action.variant]: {
          carts: null,
          state: 'loading',
          error: undefined,
          lereVersion: undefined,
        },
      },
    })
  },
  [DISMISS_LERE_PERSONALISED_ABANDONED_CARTS]: (state, action) => {
    const variant = action.variant as App.LerePersonalisedAbandonedCartsVariant
    const currentCartsState = state.personalisedAbandonedCarts[variant]

    const newCarts = (currentCartsState.carts || []).filter(
      cart => cart.transactionKey !== (action.cart as AbandonedCartViewModel).transactionKey,
    )

    return {
      ...state,
      personalisedAbandonedCarts: {
        ...state.personalisedAbandonedCarts,
        [variant]: {
          carts: newCarts,
          state: newCarts.length > 0 ? currentCartsState.state : 'loading',
          error: undefined,
        },
      },
    }
  },
  [FETCH_LERE_PERSONALISED_CONTEXT]: (state) => ({
    ...state,
    personalisedContext: {
      offerIds: null,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME]: (state) => ({
    ...state,
    personalisedPeopleLikeMe: {
      offerIds: null,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_TOP_DEALS]: (state) => ({
    ...state,
    topDeals: {
      offerIds: null,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_HERO_PLANNER]: (state) => ({
    ...state,
    heroPlanner: {
      offerIds: null,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_HOT_LEADS]: (state) => ({
    ...state,
    hotLead: {
      data: emptyHotLead,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME_EXISTS]: (state) => ({
    ...state,
    personalisationPeopleLikeMeExists: {
      exists: null,
      state: 'loading',
      error: undefined,
      lereVersion: undefined,
    },
  }),
})

const apiSuccesses = reducerSwitch<App.RecommendationState>({
  [CACHE_LERE_RECOMMENDATIONS]: (state, action) => ({
    ...state,
    lerePrecached: {
      ...state.lerePrecached,
      [action.cacheKey]: {
        ...state.lerePrecached[action.cacheKey],
        state: 'done',
      },
    },
  }),
  [FETCH_CALENDAR_OFFER_RECOMMENDATIONS]: (state, action) => {
    if (action.requestId !== state.calendarRecommendations.requestId) {
      return state
    }
    return ({
      ...state,
      calendarRecommendations: {
        state: 'done',
        offerIds: action.data.offerIds,
        requestId: action.data.requestId,
      },
    })
  },
  [FETCH_OFFER_BASED_LERE_RECOMMENDATIONS]: (state, action) => {
    const offerIds = action.data as string[]
    const { cacheKey, mergeResults, filterKey } = action
    const newState: App.RecommendationState = {
      ...state,
      recommendations: {
        ...state.recommendations,
      },
    }
    if (mergeResults) {
      const recommendationState = newState.recommendations[cacheKey]
      const existingRecommendationOfferIds = recommendationState?.offerIds || []
      const filtersFetched = recommendationState?.filtersFetched || []
      const newOfferIds = offerIds.filter((offerId) =>
        !existingRecommendationOfferIds.includes(offerId),
      )
      const newFiltersFetched = [...filtersFetched]

      if (filterKey && !newFiltersFetched.some(filter => filter === filterKey)) {
        newFiltersFetched.push(filterKey)
      }

      newState.recommendations[cacheKey] = {
        state: 'done',
        offerIds: [
          ...existingRecommendationOfferIds,
          ...newOfferIds,
        ],
        filtersFetched: newFiltersFetched,
      }
    } else {
      newState.recommendations[action.cacheKey] = { state: 'done', offerIds, filtersFetched: [action.filterKey].filter(i => !!i) }
    }
    return newState
  },
  [FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS]: (state, action) => {
    return ({
      ...state,
      locationSeededRecommendations: {
        ...state.locationSeededRecommendations,
        [action.destinationKey]: {
          offerIds: action.data,
          state: 'done',
        },
      },
    })
  },
  [FETCH_LERE_LOCATION_FLEX_RECOMMENDATIONS]: (state, action) => {
    return ({
      ...state,
      locationFlexRecommendations: {
        ...state.locationFlexRecommendations,
        [action.cacheKey]: {
          offers: action.data,
          state: 'done',
        },
      },
    })
  },
  [FETCH_LERE_PERSONALISED_ABANDONED_CARTS]: (state, action) => {
    return ({
      ...state,
      personalisedAbandonedCarts: {
        ...state.personalisedAbandonedCarts,
        [action.variant]: {
          carts: action.data.carts,
          state: 'done',
          error: undefined,
          lereVersion: action.data.lereVersion,
        },
      },
    })
  },
  [FETCH_LERE_PERSONALISED_CONTEXT]: (state, action) => ({
    ...state,
    personalisedContext: {
      offerIds: action.data.offerIds,
      state: 'done',
      error: null,
      lereVersion: action.data.lereVersion,
    },
  }),
  [FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME]: (state, action) => ({
    ...state,
    personalisedPeopleLikeMe: {
      offerIds: action.data.offerIds,
      state: 'done',
      error: null,
      lereVersion: action.data.lereVersion,
    },
  }),
  [FETCH_LERE_TOP_DEALS]: (state, action) => ({
    ...state,
    topDeals: {
      offerIds: action.data.offerIds,
      state: 'done',
      error: null,
      lereVersion: action.data.lereVersion,
    },
  }),
  [FETCH_LERE_HERO_PLANNER]: (state, action) => ({
    ...state,
    heroPlanner: {
      offerIds: action.data.offerIds,
      state: 'done',
      error: null,
      lereVersion: action.data.lereVersion,
    },
  }),
  [FETCH_LERE_HOT_LEADS]: (state, action) => ({
    ...state,
    hotLead: {
      data: action.data.hotLeadData,
      state: 'done',
      error: null,
      lereVersion: action.data.lereVersion,
    },
  }),
  [FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME_EXISTS]: (state, action) => ({
    ...state,
    personalisationPeopleLikeMeExists: {
      exists: action.data.exists,
      state: 'done',
      error: undefined,
      lereVersion: action.data.lereVersion,
    },
  }),
})

const apiFailures = reducerSwitch<App.RecommendationState>({
  [CACHE_LERE_RECOMMENDATIONS]: (state, action) => ({
    ...state,
    lerePrecached: {
      ...state.lerePrecached,
      [action.cacheKey]: {
        ...state.lerePrecached[action.cacheKey],
        state: 'error',
      },
    },
  }),
  [FETCH_CALENDAR_OFFER_RECOMMENDATIONS]: (state, action) => {
    if (action.requestId !== state.calendarRecommendations.requestId) {
      return state
    }
    return ({
      ...state,
      calendarRecommendations: {
        state: 'error',
        error: action.error,
        offerIds: null,
        requestId: state.calendarRecommendations.requestId,
      },
    })
  },
  [FETCH_OFFER_BASED_LERE_RECOMMENDATIONS]: (state, action) => ({
    ...state,
    recommendations: {
      ...state.recommendations,
      [action.cacheKey]: {
        offerIds: null,
        state: 'error',
        error: action.error,
      },
    },
  }),
  [FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS]: (state, action) => ({
    ...state,
    locationSeededRecommendations: {
      ...state.locationSeededRecommendations,
      [action.destinationKey]: {
        offerIds: [],
        state: 'error',
        error: action.error,
      },
    },
  }),
  [FETCH_LERE_LOCATION_FLEX_RECOMMENDATIONS]: (state, action) => ({
    ...state,
    locationFlexRecommendations: {
      ...state.locationFlexRecommendations,
      [action.cacheKey]: {
        offers: [],
        state: 'error',
        error: action.error,
      },
    },
  }),
  [FETCH_LERE_PERSONALISED_ABANDONED_CARTS]: (state, action) => {
    return ({
      ...state,
      personalisedAbandonedCarts: {
        ...state.personalisedAbandonedCarts,
        [action.variant]: {
          carts: null,
          state: 'error',
          error: action.error,
          lereVersion: undefined,
        },
      },
    })
  },
  [FETCH_LERE_PERSONALISED_CONTEXT]: (state, action) => ({
    ...state,
    personalisedContext: {
      offerIds: null,
      state: 'error',
      error: action.error,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME]: (state, action) => ({
    ...state,
    personalisedPeopleLikeMe: {
      offerIds: null,
      state: 'error',
      error: action.error,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_TOP_DEALS]: (state, action) => ({
    ...state,
    topDeals: {
      offerIds: null,
      state: 'error',
      error: action.error,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_HERO_PLANNER]: (state, action) => ({
    ...state,
    topDeals: {
      offerIds: null,
      state: 'error',
      error: action.error,
      lereVersion: undefined,
    },
  }),
  [FETCH_LERE_HOT_LEADS]: (state, action) => (
    state.hotLead.state === 'done' ? state : {
      ...state,
      hotLead: {
        data: emptyHotLead,
        state: 'error',
        error: action.error,
        lereVersion: undefined,
      },
    }),
  [FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME_EXISTS]: (state, action) => ({
    ...state,
    personalisationPeopleLikeMeExists: {
      exists: null,
      state: 'error',
      error: action.data.error,
      lereVersion: undefined,
    },
  }),
})

const recommendationReducer = createReducer<App.RecommendationState>(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiRequests(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailures(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccesses(action.api)(state, action),
  [SET_OFFER_LERE_INLINE_YMAL_IN_VIEW]: (state, action) => {
    const offerId = action.payload.offerId
    return {
      ...state,
      offerSharedState: {
        [offerId]: {
          ymalInlineInView: action.payload.ymalInlineInView,
          checkIn: state.offerSharedState[offerId]?.checkIn,
          checkOut: state.offerSharedState[offerId]?.checkOut,
          duration: state.offerSharedState[offerId]?.duration,
        },
      },
    }
  },
  [SET_OFFER_CHECK_IN_CHECK_OUT]: (state, action) => {
    const offerId = action.payload.offerId
    return {
      ...state,
      offerSharedState: {
        [offerId]: {
          ymalInlineInView: state.offerSharedState[offerId]?.ymalInlineInView ?? false,
          checkIn: action.payload.checkIn,
          checkOut: action.payload.checkOut,
          duration: action.payload.duration,
        },
      },
    }
  },
  [CLEAR_OFFER_CHECK_IN_CHECK_OUT]: (state, action) => {
    const updatedState: App.RecommendationState = {
      ...state,
      offerSharedState: {
        ...state.offerSharedState,
      },
    }
    delete updatedState[action.payload.offerId]
    return updatedState
  },
  [SET_LERE_VERSION]: (state, action) => {
    return {
      ...state,
      lereVersion: action.lereVersion,
    }
  },
  [CACHE_LERE_RECOMMENDATIONS]: (state, action) => {
    return {
      ...state,
      lerePrecached: {
        ...state.lerePrecached,
        [action.cacheKey]: true,
      },
    }
  },
  [OFFER_CALENDAR_RECOMMENDATIONS_CLEAR]: (state) => {
    const newState: App.RecommendationState = { ...state }
    newState.calendarRecommendations = { state: 'loading', offerIds: null, requestId: -1 }
    return newState
  },
  [SAVE_LERE_HOT_LEADS]: (state, action) => ({
    ...state,
    hotLead: {
      data: action.hotLeadData,
      state: 'done',
      error: undefined,
      lereVersion: 'sticky-hl-local',
    },
  }),
  [SAVE_DISMISSED_ABANDONED_CART_OFFER_IDS]: (state, action) => {
    const currentIds = state.dismissedAbandonedCart.offerIds
    const idsToAdd = action.offerIds

    return {
      ...state,
      dismissedAbandonedCart: {
        offerIds: unique([...currentIds, ...idsToAdd]),
      },
    }
  },
  [REMOVE_DISMISSED_ABANDONED_CART_OFFER_IDS]: (state, action) => {
    const currentIds = state.dismissedAbandonedCart.offerIds
    const idsToRemove = action.offerIds

    return {
      ...state,
      dismissedAbandonedCart: {
        offerIds: without(currentIds, ...idsToRemove),
      },
    }
  },
})

export default recommendationReducer
